import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

type AddableStepType = Api.AutomationStepType | 'TriggerAutomation';

interface Params {
	templateId: string;
	stepType: AddableStepType;
	step: Api.IAutomationStep;
	/** Insert the new step before the step with this ID (append when omitted) */
	beforeId?: string;
	impersonationContext?: Api.IImpersonationContext;
	relatedTemplateIds?: string[];
}

export function useAutomationTemplateAddStepMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationStep, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ templateId, stepType, step, beforeId, impersonationContext }: Params) => {
			const queryParams = beforeId ? `?beforeId=${encodeURIComponent(beforeId)}` : '';
			return userSession.webServiceHelper.callAsync<Api.IAutomationStep>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Step/${encodeURIComponent(
						stepType
					)}${queryParams}`,
					impersonationContext,
				}),
				'POST',
				step
			);
		},
		onError,
		onSuccess: (data, variables) => {
			const templateIdsToInvalidate = new Set([variables.templateId, ...(variables.relatedTemplateIds ?? [])]);
			for (const templateId of templateIdsToInvalidate) {
				queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
				invalidateAutomationTemplateDraftStateQuery(templateId);
			}
			onSuccess?.(data, variables);
		},
	});
}
